"use client";
import { useId, useState, type ReactNode } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import {
  HelpCircle, ChevronDown, MinusCircle,
  Home, Layers, ShieldCheck, Wrench, Headphones,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate, type LangCode } from "@/lib/i18n";
import RichText from "./RichText";

export type FAQItem = { question: string; answer: string };

export type FAQCategory = {
  id: string;
  label: string;
  icon: LucideIcon;
  items: FAQItem[];
};

/* The site-wide default set — used by the marketing home page. Pages with
   their own subject matter (pricing, help center) pass their own categories
   so every FAQ on the site still renders through this one component.

   Only the id and the icon live here; labels and Q&A come from the
   dictionaries, keyed by id. Pass-in categories from other pages are used
   verbatim and are NOT translated — those pages are still English-only. */
const DEFAULT_FAQ_META: { id: string; icon: LucideIcon }[] = [
  { id: "general", icon: Home },
  { id: "features", icon: Layers },
  { id: "security", icon: ShieldCheck },
  { id: "implementation", icon: Wrench },
  { id: "support", icon: Headphones },
];

const buildDefaultCategories = (lang: LangCode): FAQCategory[] =>
  DEFAULT_FAQ_META.map(({ id, icon }) => ({
    id,
    icon,
    label: translate<string>(lang, `faq.categories.${id}.label`),
    items: translate<FAQItem[]>(lang, `faq.categories.${id}.items`),
  }));

/** The English set. Still exported for anything that wants the raw questions. */
export const DEFAULT_FAQ_CATEGORIES: FAQCategory[] = buildDefaultCategories("en");

type FAQSectionProps = {
  /** Overrides the default two-tone heading. */
  heading?: ReactNode;
  /** Defaults to the site-wide set. A single category hides the tab rail. */
  categories?: FAQCategory[];
  /**
   * Emit FAQPage JSON-LD for these questions. On by default; set false on a
   * second FAQ block sharing a page so the URL doesn't declare two FAQPage
   * entities.
   */
  structuredData?: boolean;
};

/**
 * FAQPage JSON-LD for every question in every category.
 *
 * All of it lives on this one URL — the category rail swaps which answers are
 * mounted, but it never navigates — which satisfies Google's rule that marked-up
 * FAQ content must be reachable on the source page.
 *
 * Worth setting expectations: since Google's August 2023 change, FAQ rich
 * results are limited to government and health sites, so this will not put
 * dropdowns in Google's results for us. It still earns its keep — Bing renders
 * FAQ results, and AI answer surfaces read this markup to extract answers
 * verbatim, which is where the questions below are most likely to show up.
 */
const buildFaqLd = (categories: FAQCategory[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: categories.flatMap((cat) =>
    cat.items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  ),
});

export default function FAQSection({
  heading,
  categories: categoriesProp,
  structuredData = true,
}: FAQSectionProps) {
  const { lang, t } = useLanguage();

  /* Callers that pass their own set get it untouched. Only the default set
     follows the site language. */
  const categories = categoriesProp ?? buildDefaultCategories(lang);

  /* JSON-LD is deliberately pinned to English. It describes the page for
     crawlers, and the page a crawler sees is always the English one — the
     language switch is client-side and never changes the URL. Emitting Hindi
     markup after a visitor toggles would describe a document Google can't
     fetch. */
  const structuredDataSource = categoriesProp ?? DEFAULT_FAQ_CATEGORIES;

  const [activeCategory, setActiveCategory] = useState<string>(categories[0]?.id ?? "");
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  /* Scopes the sliding tab highlight to this instance — a bare string would
     make two FAQ sections on one page animate into each other. */
  const highlightId = useId();

  /* One category means there is nothing to switch between, so the rail is
     dropped and the accordion runs down the middle instead. */
  const showTabs = categories.length > 1;
  const current = categories.find((c) => c.id === activeCategory) ?? categories[0];
  const currentFaqs = current?.items ?? [];

  const tabsContainerVariants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } },
  };

  const tabItemVariants = {
    hidden: { opacity: 0, x: -14 },
    show: { opacity: 1, x: 0, transition: { type: "spring" as const, stiffness: 320, damping: 26 } },
  };

  const listContainerVariants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.06 } },
    exit: { transition: { staggerChildren: 0.03, staggerDirection: -1 } },
  };

  const listItemVariants = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 300, damping: 28 } },
    exit: { opacity: 0, y: -8, transition: { duration: 0.15 } },
  };

  const toggleExpand = (idx: number) => {
    setExpandedIndex(expandedIndex === idx ? null : idx);
  };

  return (
    <section id="faq" className="w-full scroll-mt-24 py-12 sm:py-16 lg:py-24 bg-[#FAFCFF] overflow-hidden relative">
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildFaqLd(structuredDataSource)) }}
        />
      )}
      <div className="site-container relative z-10">

        {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-8 sm:mb-14"
          >
            <h2 className="font-heading section-heading text-[#0d1117]">
              {heading ?? <RichText text={t("faq.heading")} breakClassName="block sm:hidden" />}
            </h2>
          </motion.div>

          {/* 2-Column Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-10">

            {/* LEFT COLUMN: Category Tabs (Horizontal scroll on mobile, vertical on desktop) */}
            {showTabs && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-4 lg:border-r lg:border-dashed lg:border-slate-200/90 lg:pr-8"
            >
              <motion.div
                variants={tabsContainerVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-row flex-wrap lg:flex-col gap-2.5 sm:gap-3 pb-4 lg:pb-0"
              >
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = activeCategory === cat.id;
                  return (
                    <motion.button
                      key={cat.id}
                      type="button"
                      variants={tabItemVariants}
                      whileHover={{ x: isActive ? 0 : 4 }}
                      whileTap={{ scale: 0.97 }}
                      transition={{ type: "spring", stiffness: 420, damping: 30 }}
                      onClick={() => {
                        setActiveCategory(cat.id);
                        setExpandedIndex(0); // Open first question of category by default
                      }}
                      className={`relative flex items-center gap-2 sm:gap-3.5 p-2 sm:p-4 pr-4 sm:pr-6 rounded-xl sm:rounded-2xl cursor-pointer shrink-0 snap-start text-left focus:outline-none ${
                        isActive
                          ? "text-[#0d1117] font-bold"
                          : "bg-slate-50/80 lg:bg-transparent text-slate-600 hover:bg-slate-100 lg:hover:bg-slate-100/70 font-medium transition-colors duration-300"
                      }`}
                    >
                      {/* Sliding active highlight */}
                      {isActive && (
                        <motion.span
                          layoutId={`faq-tab-highlight-${highlightId}`}
                          transition={{ type: "spring", stiffness: 380, damping: 32 }}
                          className="absolute inset-0 rounded-xl sm:rounded-2xl bg-[#EFF3FE] border border-[#3D5EE1]/20 shadow-xs"
                        />
                      )}

                      <motion.div
                        animate={isActive ? { scale: 1.06, rotate: 0 } : { scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 400, damping: 20 }}
                        className={`relative z-10 w-7 h-7 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0 transition-colors duration-300 ${
                          isActive
                            ? "bg-[#3D5EE1] text-white shadow-sm"
                            : "bg-[#3D5EE1]/10 text-[#3D5EE1]"
                        }`}
                      >
                        <Icon size={14} className="sm:hidden" />
                        <Icon size={18} className="hidden sm:block" />
                      </motion.div>
                      <span className="relative z-10 text-[12px] sm:text-[15px] whitespace-nowrap">{cat.label}</span>
                    </motion.button>
                  );
                })}
              </motion.div>
            </motion.div>
            )}

            {/* RIGHT COLUMN: Accordion List for Selected Category (68% width) */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className={showTabs ? "lg:col-span-8" : "lg:col-span-12 w-full max-w-3xl mx-auto"}
            >
              <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeCategory}
                variants={listContainerVariants}
                initial="hidden"
                animate="show"
                exit="exit"
                className="space-y-3.5"
              >
                {currentFaqs.map((faq, idx) => {
                  const isExpanded = expandedIndex === idx;
                  const CategoryIcon = current?.icon || HelpCircle;

                  return (
                    <motion.div
                      key={idx}
                      variants={listItemVariants}
                      className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                        isExpanded
                          ? "bg-white border-[#3D5EE1]/18 shadow-[0_10px_30px_-14px_rgba(61,94,225,0.4)]"
                          : "bg-white/70 backdrop-blur-sm border-slate-900/[0.05] hover:bg-white hover:border-slate-900/[0.08] shadow-[0_2px_10px_-8px_rgba(15,23,42,0.25)]"
                      }`}
                    >
                      {/* Accordion Question Header */}
                      <button
                        onClick={() => toggleExpand(idx)}
                        className="w-full flex items-center justify-between p-3.5 sm:p-5 text-left focus:outline-none cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5 sm:gap-3.5 pr-2">
                          <div
                            className={`w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg flex items-center justify-center shrink-0 ${
                              isExpanded
                                ? "bg-[#3D5EE1]/15 text-[#3D5EE1]"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {isExpanded ? <MinusCircle size={14} className="sm:w-4 sm:h-4" /> : <CategoryIcon size={13} className="sm:w-[15px] sm:h-[15px]" />}
                          </div>
                          <span className="font-sora font-bold text-[12.5px] sm:text-base text-[#0d1117] leading-snug">
                            {faq.question}
                          </span>
                        </div>

                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ type: "spring", stiffness: 300, damping: 24 }}
                          className={`shrink-0 ml-1 sm:ml-3 ${isExpanded ? "text-[#3D5EE1]" : "text-slate-400"}`}
                        >
                          <ChevronDown size={16} className="sm:w-[18px] sm:h-[18px]" />
                        </motion.div>
                      </button>

                      {/* Accordion Answer Content */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                          >
                            <div className="px-3.5 sm:px-6 pb-4 sm:pb-5 pt-0.5 sm:pt-1 text-[11px] sm:text-sm text-slate-600 font-medium leading-relaxed pl-[42px] sm:pl-14">
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </motion.div>
              </AnimatePresence>
            </motion.div>

          </div>

          {/* Bottom Footer */}
          <div className="pt-6 border-t border-slate-900/[0.06] text-center flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-600">
            <Headphones size={18} className="text-[#3D5EE1]" />
            <span>{t("faq.stillNeedHelp")}</span>
            {/* Routes to /contact, not `#contact`: the `id="contact"` section
                lives in DemoSection, which only the /contact page renders — so
                the old anchor had no target on the page the FAQ sits on. */}
            <Link href="/contact" className="text-[#3D5EE1] font-bold underline hover:text-[#2A48C9] transition-colors">
              {t("faq.contactSupport")}
            </Link>
          </div>

      </div>
    </section>
  );
}

